import { API_KEYS } from '@trip-ux/constants';
import { toNumber, get, map, isEmpty, first } from 'lodash';
import { abs, add } from 'mathjs';
import { PolicyRequest } from '../policy-create';
import {
  RESET_QUOTE,
  RE_QUOTE_FAILED,
  RE_QUOTE_START,
  RE_QUOTE_SUCCESS,
  ENDORSEMENT_INITIATE_TOKEN,
  ENDORSEMENT_PAYMENT_DETAILS,
  POLICY_UPDATE_START,
  POLICY_UPDATE_SUCCESS,
  POLICY_UPDATE_FAILED,
  POLICY_RETRY_START,
  POLICY_RETRY_SUCCESS,
  POLICY_RETRY_FAILED,
  SHOW_APPLY_CHANGES,
  PolicyUpdate,
  EndorsementPayment
} from './types';
import { POLICY_INFO, POLICY_INFO_EDIT } from '../policy-info';
import { TOKENIZATION_STATE } from '../traveller-payments/types';

export const getPaymentAmount = (amount: number, paymentType: string): number =>
  (paymentType === API_KEYS.CHARGE ? 1 : -1) * abs(amount);

const getError = (payload: unknown): { message: string; errorRef?: string } => {
  const error = get(payload, 'response.data.error', '');
  const errMessages = get(payload, 'response.data.errors', []);
  if (errMessages.length > 1 || isEmpty(errMessages)) {
    return { message: error, errorRef: get(first(errMessages), 'errorRef') };
  } else {
    return {
      message: get(first(errMessages), 'message'),
      errorRef: get(first(errMessages), 'errorRef')
    };
  }
};

export const policyUpdate = (
  state: PolicyUpdate = {
    deltaTotal: 0,
    endorsementPayment: {
      tokenState: TOKENIZATION_STATE.NOT_STARTED
    }
  },
  action: {
    type: string;
    payload?: unknown;
  }
): PolicyUpdate => {
  switch (action.type) {
    case POLICY_INFO:
    case POLICY_INFO_EDIT:
      return { deltaTotal: 0, endorsementPayment: { tokenState: TOKENIZATION_STATE.NOT_STARTED } };
    case SHOW_APPLY_CHANGES:
      return { ...state, showApplyChanges: action.payload as boolean };
    case POLICY_UPDATE_FAILED: {
      const { message: updateMsg, errorRef: updateRef } = getError(get(action, 'payload.e'));
      return { ...state, updateLoading: false, updateError: { errorMessage: updateMsg, errorRef: updateRef } };
    }
    case POLICY_UPDATE_START:
      return { ...state, updateLoading: true, updateError: undefined };
    case POLICY_UPDATE_SUCCESS:
      return { ...state, updateLoading: false, updateResponse: action.payload as PolicyRequest };
    case POLICY_RETRY_FAILED: {
      const { message: retryMsg, errorRef: retryRef } = getError(get(action, 'payload.e'));
      return { ...state, retryLoading: false, retryError: { errorMessage: retryMsg, errorRef: retryRef }, retryResponse: undefined };
    }
    case POLICY_RETRY_START:
      return { ...state, retryLoading: true, retryError: undefined, retryResponse: undefined };
    case POLICY_RETRY_SUCCESS:
      return { ...state, retryLoading: false, retryResponse: action.payload as PolicyRequest, retryError: undefined };
    case RESET_QUOTE:
      return { ...state, requoteDone: false };
    case RE_QUOTE_FAILED: {
      const { message: requoteMsg, errorRef: requoteRef } = getError(get(action, 'payload.e'));
      return {
        ...state,
        requoteLoading: false,
        requoteError: {
          changeRequest: get(action, 'payload.changeRequest'),
          errorMessage: requoteMsg,
          errorRef: requoteRef
        }
      };
    }
    case RE_QUOTE_START:
      return { ...state, requoteLoading: true, requoteError: undefined };
    case RE_QUOTE_SUCCESS: {
      const policyResponse = action.payload as PolicyRequest;
      let total = 0;

      const deltaPaxes = map(policyResponse.paxes, pax => {
        total = toNumber(add(total, pax.policyDelta.deltaAmount).valueOf());
        return pax.policyDelta;
      });

      return {
        ...state,
        requoteLoading: false,
        requoteResponse: action.payload as PolicyRequest,
        requoteError: undefined,
        deltaTotal: total,
        deltaPaxes,
        requoteDone: true
      };
    }
    case ENDORSEMENT_INITIATE_TOKEN: {
      const { endorsementPayment, ...rest } = state;
      return {
        ...rest,
        endorsementPayment: {
          ...endorsementPayment,
          tokenState: TOKENIZATION_STATE.INITIATED
        }
      };
    }
    case ENDORSEMENT_PAYMENT_DETAILS: {
      const act = action.payload as EndorsementPayment;
      const { endorsementPayment, ...rest } = state;
      return {
        ...rest,
        endorsementPayment: {
          ...act,
          tokenState:
            get(endorsementPayment, 'tokenState') === TOKENIZATION_STATE.INITIATED
              ? TOKENIZATION_STATE.COMPLETED
              : TOKENIZATION_STATE.NOT_STARTED
        }
      };
    }
    default:
      return state;
  }
};
